/* ══════════════════════════════════════════════════════════════════════
   Skön Cosmetics — skon-fs-suggestions.js
   asset: assets/skon-fs-suggestions.js
   Modulo: Free Shipping Test (FS) — Widget Cross-sell
   Versione: 1.1.1 — 28 maggio 2026 — 00:59

   CHANGELOG
   ─────────
   1.1.1 (28 mag 2026) — Pulizia commenti + esternalizzazione API version
     - Corretto refuso data "giugno 2026" → "maggio 2026" nel commento
       della strategia 'closest_then_ascending' di selectProducts
       (la data era nel futuro al momento della scrittura).
     - Estratta la versione Storefront API di Shopify ('2026-04') dal
       codice hardcoded di _enrichWithDiscounts. Ora letta da
       CFG.shopify.storefrontApiVersion (skon-fs-config), con fallback
       a '2026-04' come safety in caso di config non aggiornata.
       Nessuna modifica di comportamento: stessa stringa di versione,
       solo origine diversa.

   1.1 (21 mag 2026) — FIX PAYLOAD CustomEvent 'skon-fs:filler-added'
     Bug pre-esistente in v1.0: il payload del CustomEvent usava chiavi
     camelCase (productHandle, variantId, priceCents) mentre il listener
     in skon-fs-tracking.js v1.0/1.1 (e ora skon-fs-test.js v1.2) leggeva
     chiavi snake_case (product_handle, variant_id, price_cents,
     subtotal_before_cents, visitor_type).
     Conseguenza: gli eventi GA4 'skon_fs_filler_added' arrivavano con
     fs_filler_handle="", fs_filler_price_eur="0.00", fs_filler_variant=""
     rendendo inutilizzabili le analytics per il widget cross-sell.

     Fix:
     - Convertite tutte le chiavi del payload a snake_case (allineate
       alla convenzione del listener).
     - Aggiunti i 2 campi mancanti: subtotal_before_cents (letto da
       SkonFS.cart._lastSubtotal PRIMA del refresh post-add) e
       visitor_type (letto da SkonFS.id.getVisitorType()).
     - Per garanzia di backward-compat, sono mantenute ANCHE le vecchie
       chiavi camelCase: il payload include entrambe le convenzioni.
       Costo trascurabile (4 chiavi extra), zero rischio di rotture per
       eventuali ascoltatori esterni che leggono il payload v1.0.

   1.0 (mag 2026):
     - Release iniziale del widget cross-sell.

   COSA FA QUESTO FILE
   ───────────────────
   Renderizza il widget di suggerimento prodotti dentro al cart drawer:
   "Sei a un passo dalla spedizione gratuita, aggiungi uno di questi"
   con 4-6 card prodotto pescate dalla collection filler.

   CARATTERISTICHE
   ───────────────
   - Carica i prodotti via Shopify Ajax API (GET /collections/HANDLE/products.json)
   - Filtra prodotti già nel carrello (configurabile in skon-fs-config)
   - Filtra out-of-stock (configurabile)
   - Strategia di selezione: 'closest_to_gap' | 'cheapest_first' | 'mixed' | 'closest_then_ascending'
   - Add-to-cart inline senza chiudere il drawer
   - Aggiorna in tempo reale al variare del subtotal
   - Si nasconde se subtotal ≥ soglia (caso edge)

   DIPENDENZE
   ──────────
   Questo file richiede skon-fs-test.js già caricato (per accedere a
   window.SkonFS namespace e in particolare a SkonFS.cart, SkonFS.ab,
   SkonFS.id).

   DEPLOYMENT: caricare DOPO skon-fs-test.js in theme.liquid (defer).
══════════════════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  // Aspetta che SkonFS sia disponibile (può essere differito per defer order)
  function _waitForSkonFS(callback, attempts) {
    attempts = attempts || 0;
    if (window.SkonFS && window.SkonFS._cfg) return callback();
    if (attempts > 50) return; // max 5 secondi di attesa
    setTimeout(function() { _waitForSkonFS(callback, attempts + 1); }, 100);
  }


  function _init() {

    const SkonFS = window.SkonFS;
    const CFG = SkonFS._cfg;

    // Guard: se il modulo non si è attivato, non rendiamo nulla
    if (!SkonFS.id.shouldActivate()) return;


    // ──────────────────────────────────────────────────────────────────
    // Utilities
    // ──────────────────────────────────────────────────────────────────

    function _log() {
      if (!CFG.debug || !CFG.debug.verbose) return;
      const args = Array.prototype.slice.call(arguments);
      args.unshift('[skon-fs:suggestions]');
      console.info.apply(console, args);
    }

    function _formatEUR(cents) {
      // SKÖN custom (maggio 2026 — coerenza currency drawer): prefix
      // "€" senza spazio, coerente con il filtro Liquid `money` usato
      // dal resto del cart (es. "€12,60" non "12,60 €"). La progress
      // bar ha la sua _formatEUR locale dentro skon-fs-test.js che NON
      // va modificata (la progress bar mostra "Aggiungi 3,93 € per..."
      // con format suffix di scelta esplicita).
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }

    function _interpolate(template, values) {
      return template.replace(/\{\{(\w+)\}\}/g, function(_, key) {
        return key in values ? values[key] : '';
      });
    }


    // ──────────────────────────────────────────────────────────────────
    // SkonFS.suggestions — namespace
    // ──────────────────────────────────────────────────────────────────

    SkonFS.suggestions = {

      /**
       * Cache dei prodotti caricati per handle/source (TTL session).
       * Chiavi: 'collection:{handle}', 'related:{cartFingerprint}'
       * Ogni chiave mappa a un array di prodotti normalizzati via _normalizeProduct.
       */
      _productsCache: {},

      /**
       * Cache dei prodotti caricati dalla collection filler.
       * Backward-compat: vecchio _productsCache mantenuto come getter
       * dinamico per evitare di rompere consumatori esterni che lo
       * leggono direttamente.
       */
      get _productsCacheLegacy() {
        return this._productsCache['collection:' + (CFG.filler.collectionHandle || '')];
      },

      /**
       * Normalizza un prodotto Shopify (Ajax API) al modello interno
       * usato dal widget. Estratto da loadProducts per essere riusato
       * da loadCollection (free-shipping/bestseller) e loadRelated
       * (modalità correlati). Garantisce che tutte le sorgenti di
       * prodotto producano lo stesso shape consumato da renderWidget.
       */
      _normalizeProduct: function(p) {
        if (!p || !p.variants || !p.variants.length) return null;
        // Prendi la prima variante disponibile come default
        const firstAvailable = p.variants.find(function(v) { return v.available; }) || p.variants[0];

        // SKÖN custom (maggio 2026) — Normalizzazione prezzo agnostica
        // al formato di provenienza:
        //
        //   - /collections/{handle}/products.json restituisce price come
        //     STRING decimale (es. "25.00") → va moltiplicato per 100
        //   - /recommendations/products.json restituisce price come NUMBER
        //     già in cents (es. 2400) → va usato direttamente
        //
        // Se non distinguiamo i due casi, i recommendations vengono
        // moltiplicati per 100 producendo prezzi assurdi tipo "2400,00 €"
        // invece di "24,00 €". Lo stesso vale per compare_at_price.
        function _toCents(v) {
          if (v === null || v === undefined || v === '') return 0;
          if (typeof v === 'number') return Math.round(v); // già in cents
          return Math.round(parseFloat(v) * 100);          // string decimale
        }

        const priceCents   = _toCents(firstAvailable.price);
        const compareCents = _toCents(firstAvailable.compare_at_price);
        const isOnSale     = compareCents > priceCents;

        // SKÖN custom (maggio 2026) — Normalizzazione URL immagine
        // agnostica al formato di provenienza:
        //
        //   - /collections/.../products.json:
        //       images[0] è OBJECT con .src ("https://cdn.shopify.com/...")
        //       featured_image è OBJECT
        //   - /recommendations/products.json:
        //       images[0] è STRING ("//cdn.shopify.com/...", protocol-relative)
        //       featured_image è STRING
        //
        // Strategia: priorità a featured_image (entrambi gli endpoint
        // lo espongono in formato consistente per la propria sorgente),
        // fallback a images[0]. Per ogni candidato controlliamo il tipo:
        // se string usiamo direttamente, se object leggiamo .src.
        // Infine prefissiamo "https:" se l'URL è protocol-relative ("//...").
        function _extractImageUrl(candidate) {
          if (!candidate) return '';
          let url = '';
          if (typeof candidate === 'string') url = candidate;
          else if (typeof candidate === 'object' && candidate.src) url = candidate.src;
          if (url && url.indexOf('//') === 0) url = 'https:' + url;
          return url;
        }
        const imageUrl =
          _extractImageUrl(p.featured_image) ||
          _extractImageUrl(p.images && p.images[0]) ||
          '';

        return {
          id:            p.id,
          handle:        p.handle,
          title:         p.title,
          description:   (p.body_html || '').replace(/<[^>]*>/g, '').slice(0, 80),
          priceCents:    priceCents,
          priceFmt:      _formatEUR(priceCents),
          compareCents:  compareCents,
          compareFmt:    isOnSale ? _formatEUR(compareCents) : '',
          isOnSale:      isOnSale,
          imageUrl:      imageUrl,
          variantId:     firstAvailable.id,
          available:     firstAvailable.available,
          variantsCount: (p.variants || []).length,
        };
      },

      /**
       * Carica prodotti da una collection arbitraria via handle.
       * Cached per handle, TTL session. Riusabile sia per la collection
       * filler (modalità free-shipping) sia per la collection bestseller
       * (modalità cart-vuoto).
       */
      _loadCollection: async function(handle) {
        if (!handle) {
          _log('_loadCollection: no handle');
          return [];
        }
        const cacheKey = 'collection:' + handle;
        if (this._productsCache[cacheKey]) return this._productsCache[cacheKey];

        try {
          const url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=30';
          const res = await fetch(url, { credentials: 'same-origin' });
          const data = await res.json();
          const products = (data && data.products) || [];
          const normalized = products.map(this._normalizeProduct).filter(function(p) { return p !== null; });
          this._productsCache[cacheKey] = normalized;
          _log('_loadCollection: loaded', normalized.length, 'products from', handle);
          return normalized;
        } catch (e) {
          _log('_loadCollection failed', handle, e);
          return [];
        }
      },

      /**
       * Carica prodotti correlati a una lista di product_id, via Shopify
       * /recommendations/products.json (intent=related). Fa max N chiamate
       * parallele (CFG.widgetModes.relatedMaxApiCalls), de-duplica i
       * risultati per id. Cache short-lived basata sul fingerprint del
       * cart per evitare chiamate ripetute durante refresh consecutivi.
       */
      _loadRelated: async function(productIds) {
        if (!productIds || !productIds.length) return [];
        const maxCalls = (CFG.widgetModes && CFG.widgetModes.relatedMaxApiCalls) || 2;
        const ids = productIds.slice(0, maxCalls);
        const cacheKey = 'related:' + ids.slice().sort().join(',');
        if (this._productsCache[cacheKey]) return this._productsCache[cacheKey];

        try {
          const requests = ids.map(function(id) {
            const url = '/recommendations/products.json?product_id=' + encodeURIComponent(id) + '&limit=10&intent=related';
            return fetch(url, { credentials: 'same-origin' })
              .then(function(r) { return r.json(); })
              .then(function(d) { return (d && d.products) || []; })
              .catch(function() { return []; });
          });
          const responses = await Promise.all(requests);

          // Concatena tutti i risultati, normalizza, de-duplica per id
          const seen = {};
          const merged = [];
          const self = this;
          responses.forEach(function(arr) {
            arr.forEach(function(p) {
              if (seen[p.id]) return;
              seen[p.id] = true;
              const norm = self._normalizeProduct(p);
              if (norm) merged.push(norm);
            });
          });

          this._productsCache[cacheKey] = merged;
          _log('_loadRelated: loaded', merged.length, 'unique products for cart ids', ids);
          return merged;
        } catch (e) {
          _log('_loadRelated failed', e);
          return [];
        }
      },

      /**
       * Wrapper retro-compatibile: alcune routine esterne potrebbero
       * chiamare loadProducts() direttamente. Mantiene il comportamento
       * storico (carica la collection filler) come default.
       */
      loadProducts: async function() {
        return this._loadCollection(CFG.filler.collectionHandle);
      },

      /**
       * Selezione prodotti da mostrare in funzione di gap, strategia, esclusioni.
       */
      selectProducts: async function(gapCents) {
        const all = await this.loadProducts();
        if (!all.length) return [];

        let pool = all.slice();

        // Filtro out-of-stock
        if (CFG.filler.excludeOutOfStock) {
          pool = pool.filter(function(p) { return p.available; });
        }

        // Filtro prodotti già in carrello
        if (CFG.filler.excludeAlreadyInCart) {
          const cartProductIds = await this._getCartProductIds();
          pool = pool.filter(function(p) {
            return cartProductIds.indexOf(p.id) === -1;
          });
        }

        // Filtro prodotti con più varianti — vedi commento config.
        // Se variantsCount > 1, il prodotto richiede una scelta valutativa
        // (es. tonalità pelle del fondotinta) che non vogliamo gestire
        // dentro il drawer cart. Lo escludiamo dal widget cross-sell.
        if (CFG.filler.excludeMultiVariant) {
          pool = pool.filter(function(p) { return p.variantsCount <= 1; });
        }

        // SAFETY MARGIN — augmenta il gap quando ci sono sconti attivi.
        //
        // Logica: se il cart ha qualunque tipo di sconto applicato
        // (line-level OPPURE cart-level OPPURE compare_at_price ridotto
        // sulla variante), il prezzo "vero" che il visitatore pagherà
        // per un prodotto suggerito potrebbe essere inferiore al listino
        // visualizzato. Per garantire che la promessa "questo prodotto
        // chiude l'ordine alla soglia free-shipping" sia sempre vera
        // anche dopo l'eventuale applicazione dello sconto al filler,
        // aumentiamo il gap richiesto del margin% configurato.
        //
        // Il margin viene preso da CFG.filler.priceMarginWhenDiscounted
        // (default 0.30 = 30%, coerente con scontistica media Skön).
        //
        // Quando NON ci sono sconti attivi, gapAdjusted == gapCents
        // (comportamento originale).
        //
        // SKÖN custom (maggio 2026 — rev. compare_at_price): esteso
        // il trigger ai compare_at scontati. Shopify NON include
        // compare_at_price nella discount_application logic (è uno
        // sconto "incorporato" nel listino della variante, non un
        // discount allocato a runtime), quindi total_discount e
        // cart_level_discount_applications restano vuoti anche se
        // il visitatore sta pagando meno del listino. Senza questo
        // fix, in una promo "Black Week" gestita via compare_at, il
        // widget potrebbe suggerire filler da €10 per chiudere un
        // gap di €10, ma il filler stesso essendo in promo verrebbe
        // pagato €7, lasciando il cart sotto-soglia e mancando la
        // promessa "questo prodotto chiude l'ordine".
        //
        // Il check sui cart items rileva qualunque item del cart con
        // compare_at_price > price. Anche un solo item compare_at è
        // sufficiente per attivare il margin: se la promo è attiva
        // sulla collection filler, è probabile che anche i filler
        // suggeriti ne facciano parte.
        const lastCart = SkonFS.cart._lastCart;
        const hasCompareAtDiscount = lastCart && Array.isArray(lastCart.items) &&
          lastCart.items.some(function(it) {
            const compareAt = it.variant_compare_at_price || (it.variant && it.variant.compare_at_price) || 0;
            const price     = it.variant_price            || (it.variant && it.variant.price)            || it.price || 0;
            return compareAt > price && price > 0;
          });
        const hasDiscount = lastCart && (
          (lastCart.total_discount && lastCart.total_discount > 0) ||
          (lastCart.cart_level_discount_applications && lastCart.cart_level_discount_applications.length > 0) ||
          hasCompareAtDiscount
        );
        const margin = (CFG.filler && typeof CFG.filler.priceMarginWhenDiscounted === 'number')
          ? CFG.filler.priceMarginWhenDiscounted
          : 0;
        const gapAdjusted = hasDiscount && margin > 0
          ? Math.ceil(gapCents * (1 + margin))
          : gapCents;

        // Fallback safety: se NESSUN prodotto del pool soddisfa il gap
        // augmentato, ritorniamo al gap originale. Meglio mostrare prodotti
        // (anche se la promessa potrebbe essere imperfetta in edge case)
        // che un widget vuoto. Lo verifichiamo PRIMA dell'ordinamento.
        if (gapAdjusted !== gapCents) {
          const someoneFitsAdjusted = pool.some(function(p) {
            return p.priceCents >= gapAdjusted;
          });
          if (!someoneFitsAdjusted) {
            _log('safety margin pool empty at gap', gapAdjusted, '— fallback to original gap', gapCents);
          }
        }
        const effectiveGap = (gapAdjusted !== gapCents)
          ? (pool.some(function(p) { return p.priceCents >= gapAdjusted; }) ? gapAdjusted : gapCents)
          : gapCents;
        gapCents = effectiveGap;

        // Strategia di ordinamento
        const strat = CFG.filler.strategy || 'closest_to_gap';

        if (strat === 'cheapest_first') {
          pool.sort(function(a, b) { return a.priceCents - b.priceCents; });
        } else if (strat === 'closest_to_gap') {
          // Prodotti il cui prezzo è ≥ gap, ordinati per "vicinanza al gap"
          // (cioè il più piccolo che chiude il gap viene per primo)
          pool.sort(function(a, b) {
            const aOver = a.priceCents >= gapCents;
            const bOver = b.priceCents >= gapCents;
            // Quelli sopra il gap prima, ordinati per prezzo crescente
            if (aOver && !bOver) return -1;
            if (!aOver && bOver) return 1;
            if (aOver && bOver) return a.priceCents - b.priceCents;
            // Entrambi sotto il gap → più cari prima (riempiono di più)
            return b.priceCents - a.priceCents;
          });
        } else if (strat === 'closest_then_ascending') {
          // Strategia ibrida (richiesta utente — maggio 2026):
          // - Slot 1: il prodotto il cui prezzo è più vicino al gap mancante
          //   (idealmente lo soddisfa o lo avvicina di più). Massima rapidità
          //   di raggiungimento soglia.
          // - Slot 2..N: TUTTI gli altri prodotti del pool, in ordine
          //   crescente di prezzo (dal meno caro al più caro). Buona scoperta
          //   progressiva del catalogo.
          //
          // Esempio: gap=€18, pool=[€12, €17, €25, €32, €45, €60]
          //   → slot 1: €17 (closest)
          //   → slot 2-6: €12, €25, €32, €45, €60 (ascending)
          if (pool.length > 0) {
            // Trova l'indice del closest-to-gap
            let closestIdx = 0;
            let closestDist = Math.abs(pool[0].priceCents - gapCents);
            for (let i = 1; i < pool.length; i++) {
              const d = Math.abs(pool[i].priceCents - gapCents);
              if (d < closestDist) { closestDist = d; closestIdx = i; }
            }
            const closest = pool[closestIdx];
            // Resto del pool ordinato ascending, escludendo il closest
            const rest = pool.slice(0, closestIdx).concat(pool.slice(closestIdx + 1));
            rest.sort(function(a, b) { return a.priceCents - b.priceCents; });
            pool = [closest].concat(rest);
          }
        } else if (strat === 'mixed') {
          // Mix: 1 closest_to_gap, 1 cheapest, alternati
          const byClose = pool.slice().sort(function(a, b) {
            return Math.abs(a.priceCents - gapCents) - Math.abs(b.priceCents - gapCents);
          });
          const byCheap = pool.slice().sort(function(a, b) {
            return a.priceCents - b.priceCents;
          });
          const merged = [];
          const seen = {};
          for (let i = 0; i < Math.max(byClose.length, byCheap.length); i++) {
            if (byClose[i] && !seen[byClose[i].id]) {
              merged.push(byClose[i]); seen[byClose[i].id] = 1;
            }
            if (byCheap[i] && !seen[byCheap[i].id]) {
              merged.push(byCheap[i]); seen[byCheap[i].id] = 1;
            }
          }
          pool = merged;
        }

        return pool.slice(0, CFG.filler.maxProducts || 6);
      },

      /**
       * Legge gli ID prodotto attualmente nel carrello.
       */
      _getCartProductIds: async function() {
        try {
          const res = await fetch('/cart.js', { credentials: 'same-origin' });
          const data = await res.json();
          return (data.items || []).map(function(it) { return it.product_id; });
        } catch (e) {
          return [];
        }
      },

      /**
       * Costruisce il markup HTML del widget.
       *
       * SKÖN custom (maggio 2026) — Accetta `mode` ('freeshipping' |
       * 'related' | 'bestseller') per scegliere copy titolo/sottotitolo
       * appropriati. Default 'freeshipping' per backward-compat con
       * eventuali consumer esterni.
       */
      renderWidget: function(products, gapCents, achieved, mode) {

        mode = mode || 'freeshipping';

        // Copy dinamico per modalità (CFG.widgetCopy con fallback ai
        // copy storici CFG.copy per non rompere installazioni in cui
        // widgetCopy non è ancora configurato).
        const copyMap = (CFG.widgetCopy) || {};
        const modeCopy = copyMap[mode] || {
          title:    CFG.copy.suggestionsTitle,
          subtitle: CFG.copy.suggestionsSubtitle,
        };
        const achievedCopy = (copyMap.achieved || CFG.copy.suggestionsAchieved);

        // Caso edge: già a soglia → widget compatto di conferma
        // (solo per modalità freeshipping, perché per related/bestseller
        // mostriamo comunque le card senza badge "achieved")
        if (achieved && mode === 'freeshipping') {
          return [
            '<div class="skon-fs-suggestions skon-fs-suggestions--achieved">',
              '<div class="skon-fs-sg-achieved-badge">',
                '<span class="skon-fs-sg-check">✓</span>',
                '<span>' + achievedCopy + '</span>',
              '</div>',
            '</div>'
          ].join('');
        }

        if (!products.length) {
          return ''; // niente da mostrare
        }

        const titleInterp = _interpolate(modeCopy.title, {
          gap: _formatEUR(gapCents),
          threshold: _formatEUR(SkonFS.ab.getThreshold()),
        });

        const cards = products.map(function(p) {
          // SKÖN custom (maggio 2026) — Layout card semplificato:
          // - aspect-ratio immagine 4:3 (gestita da CSS)
          // - bottone "add" come ICONA in overlay sull'immagine, in basso
          //   a destra (risparmio spazio verticale, niente bottone nel body)
          // - body contiene SOLO titolo (sopra) + prezzo (sotto, allineato destra)
          // - i prodotti con più varianti sono già esclusi dal pool da
          //   selectProducts() via CFG.filler.excludeMultiVariant, quindi
          //   non c'è più branch needsVariantPick: tutti i prodotti del
          //   widget sono single-variant e usano il bottone add diretto
          // - prezzo barrato RIMOSSO dalla card (la promessa "chiude l'ordine"
          //   è ora garantita dal safety margin lato algoritmo, vedi
          //   selectProducts e CFG.filler.priceMarginWhenDiscounted)
          const action =
            '<button type="button" class="skon-fs-sg-action skon-fs-sg-action--add" ' +
            '        aria-label="Aggiungi al carrello" ' +
            '        data-skon-fs-add="' + p.variantId + '" ' +
            '        data-skon-fs-handle="' + p.handle + '" ' +
            '        data-skon-fs-price="' + p.priceCents + '">' +
              '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.5 4h11l-1.2 7.4a1.5 1.5 0 0 1-1.48 1.27H5.18a1.5 1.5 0 0 1-1.48-1.27L2.5 4z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M5.5 4V3a2.5 2.5 0 0 1 5 0v1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M8 7v3M6.5 8.5h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
            '</button>';

          return [
            // SKÖN custom (maggio 2026) — Card wrapper come <a> link a PDP.
            // Tap su qualunque area della card (immagine, titolo, prezzo)
            // naviga alla PDP del prodotto. Il bottone "+" interno blocca
            // propagazione + default in handleAddClick per evitare la
            // navigazione e fare solo add-to-cart silenzioso.
            // Pattern HTML5: <a> wrapper + <button> interno è valido perché
            // <button> non è semanticamente un link.
            '<a class="skon-fs-sg-card" href="/products/' + p.handle + '" aria-label="Vai a ' + p.title + '">',
              '<div class="skon-fs-sg-img">',
                p.imageUrl ? '<img src="' + p.imageUrl + '" alt="' + p.title + '" loading="lazy">' : '',
                action,
              '</div>',
              '<div class="skon-fs-sg-body">',
                '<div class="skon-fs-sg-title">' + p.title + '</div>',
                '<div class="skon-fs-sg-price">' + p.priceFmt + '</div>',
              '</div>',
            '</a>'
          ].join('');
        }).join('');

        return [
          '<div class="skon-fs-suggestions">',
            '<div class="skon-fs-sg-header">',
              '<div class="skon-fs-sg-title-main">' + titleInterp + '</div>',
              '<div class="skon-fs-sg-subtitle">' + modeCopy.subtitle + '</div>',
            '</div>',
            '<div class="skon-fs-sg-grid">' + cards + '</div>',
          '</div>'
        ].join('');
      },

      /**
       * SKÖN custom (maggio 2026) — REFACTOR ARCHITETTURALE.
       *
       * Costruisce LO SCHELETRO DOM del widget UNA SOLA VOLTA, con
       * header (titolo+sottotitolo vuoti) + grid + N slot card vuoti
       * + un blocco "achieved badge" nascosto. Da quel momento in poi,
       * gli aggiornamenti avvengono mutando solo i contenuti dei nodi
       * esistenti (titolo, prezzo, immagine, link, data attributes
       * del bottone "+").
       *
       * Razionale: prima del refactor, ogni cart change faceva
       * `el.innerHTML = nuovoHtml`, distruggendo i nodi DOM e
       * ricreandoli da zero. Il tema svuotava il placeholder durante
       * il proprio re-render, e il modulo lo ripopolava 100-200ms
       * dopo: in mezzo, l'utente vedeva il widget sparire e
       * riapparire (flicker). Inoltre, per ogni cart change spesso
       * si verificavano 2-3 mount sovrapposti (cart events multipli
       * + MutationObserver), creando "sfarfallamento" delle card 3-4
       * volte di seguito.
       *
       * Adesso: shell creato 1 volta sola. Mutazioni puntuali sui
       * nodi esistenti tramite _diffAndUpdateCards. Niente flicker,
       * niente "doppio render" (il mode dispatcher legge dallo state
       * fresco emesso da SkonFS.cart, non da _lastSubtotal stale).
       */
      _buildSuggestionsShell: function() {
        const max = (CFG.filler && CFG.filler.maxProducts) || 5;
        const slots = [];
        for (let i = 0; i < max; i++) {
          slots.push(
            '<a class="skon-fs-sg-card" data-skon-fs-slot="' + i + '" href="#" hidden>' +
              '<div class="skon-fs-sg-img">' +
                '<img alt="" loading="lazy">' +
                '<button type="button" class="skon-fs-sg-action skon-fs-sg-action--add" aria-label="Aggiungi al carrello">' +
                  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.5 4h11l-1.2 7.4a1.5 1.5 0 0 1-1.48 1.27H5.18a1.5 1.5 0 0 1-1.48-1.27L2.5 4z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M5.5 4V3a2.5 2.5 0 0 1 5 0v1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M8 7v3M6.5 8.5h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
                '</button>' +
              '</div>' +
              '<div class="skon-fs-sg-body">' +
                '<div class="skon-fs-sg-title"></div>' +
                '<div class="skon-fs-sg-price">' +
                  '<span class="skon-fs-sg-price-original"></span>' +
                  '<span class="skon-fs-sg-price-discounted" hidden></span>' +
                '</div>' +
              '</div>' +
            '</a>'
          );
        }

        const html =
          '<div class="skon-fs-suggestions" data-skon-fs-shell="1">' +
            '<div class="skon-fs-sg-header">' +
              '<div class="skon-fs-sg-title-main"></div>' +
              '<div class="skon-fs-sg-subtitle"></div>' +
            '</div>' +
            '<div class="skon-fs-sg-grid">' + slots.join('') + '</div>' +
            '<div class="skon-fs-sg-achieved-badge" hidden>' +
              '<span class="skon-fs-sg-check">✓</span>' +
              '<span class="skon-fs-sg-achieved-text"></span>' +
            '</div>' +
          '</div>';

        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.firstElementChild;
      },

      /**
       * SKÖN custom (maggio 2026) — Diff & update degli slot card
       * dello shell esistente. NESSUN innerHTML di alto livello: solo
       * cambi di textContent, src, href, dataset, hidden.
       *
       * Quando un prodotto è già allo slot N (stesso variantId), niente
       * cambia per quello slot (zero ridondanza, zero ridisegno). Quando
       * un prodotto è diverso o lo slot diventa vuoto, mutiamo solo le
       * proprietà necessarie.
       *
       * Lo shell memorizza in `dataset.lastVariants` la lista di
       * variantId attualmente esposti, così possiamo fare il diff al
       * prossimo update.
       */
      _updateSuggestionsShell: function(shell, products, mode, achieved, gapCents) {
        if (!shell) return;

        // Copy dinamico per modalità
        mode = mode || 'freeshipping';

        // SKÖN custom (maggio 2026 — rev. data-widget-mode) — Espone
        // la modalità corrente del widget come data attribute sul
        // wrapper shell, così il CSS può applicare stili condizionali
        // alla modalità senza che il JS debba conoscerli. Pattern
        // additivo: non rompe nessuna logica esistente, solo aggiunge
        // un attributo leggibile da CSS via [data-widget-mode="..."].
        // Usato attualmente per dare al widget in modalità bestseller
        // (cart vuoto) un padding extra perché in quel layout non c'è
        // la tabella prodotti sopra a fare da contesto visuale.
        shell.dataset.widgetMode = mode;

        const copyMap = (CFG.widgetCopy) || {};
        const modeCopy = copyMap[mode] || {
          title:    CFG.copy.suggestionsTitle,
          subtitle: CFG.copy.suggestionsSubtitle,
        };
        const achievedCopy = (copyMap.achieved || CFG.copy.suggestionsAchieved);

        // Header (titolo + sottotitolo) — sempre aggiornati con copy del mode corrente
        const titleInterp = _interpolate(modeCopy.title || '', {
          gap: _formatEUR(gapCents),
          threshold: _formatEUR(SkonFS.ab.getThreshold()),
        });
        const titleEl = shell.querySelector('.skon-fs-sg-title-main');
        const subtitleEl = shell.querySelector('.skon-fs-sg-subtitle');
        const headerEl = shell.querySelector('.skon-fs-sg-header');
        const gridEl = shell.querySelector('.skon-fs-sg-grid');
        const badgeEl = shell.querySelector('.skon-fs-sg-achieved-badge');
        const badgeTextEl = shell.querySelector('.skon-fs-sg-achieved-text');

        // Modalità "achieved compatta" (solo per freeshipping):
        // mostra solo il badge "Hai raggiunto la spedizione gratuita",
        // niente cards. Per related/bestseller mostriamo le card anche
        // a soglia raggiunta.
        const showAchievedCompact = (achieved && mode === 'freeshipping');

        if (showAchievedCompact) {
          // Nascondi header + grid, mostra solo badge
          if (headerEl) headerEl.setAttribute('hidden', '');
          if (gridEl) gridEl.setAttribute('hidden', '');
          if (badgeEl) {
            badgeEl.removeAttribute('hidden');
            if (badgeTextEl) badgeTextEl.textContent = achievedCopy || '';
          }
          shell.classList.add('skon-fs-suggestions--achieved');
          shell.dataset.lastVariants = '';
          return;
        }

        // Modalità normale: mostra header + grid, nascondi badge
        if (headerEl) headerEl.removeAttribute('hidden');
        if (gridEl) gridEl.removeAttribute('hidden');
        if (badgeEl) badgeEl.setAttribute('hidden', '');
        shell.classList.remove('skon-fs-suggestions--achieved');

        if (titleEl) titleEl.textContent = titleInterp;
        if (subtitleEl) subtitleEl.textContent = modeCopy.subtitle || '';

        // Diff sugli slot card.
        // Confrontiamo la nuova lista di prodotti con la precedente
        // (dataset.lastVariants) per evitare update inutili degli slot
        // identici. Però il "no-update" non è un'ottimizzazione critica
        // — il flicker visivo deriva dall'innerHTML su tutto il widget,
        // non dal cambio di textContent su una card. Anche aggiornando
        // tutto, niente flicker.
        const prevVariants = (shell.dataset.lastVariants || '').split('|');
        const slots = shell.querySelectorAll('[data-skon-fs-slot]');
        const max = slots.length;

        for (let i = 0; i < max; i++) {
          const slot = slots[i];
          const product = products[i];

          if (!product) {
            // Slot vuoto
            if (!slot.hasAttribute('hidden')) {
              slot.setAttribute('hidden', '');
            }
            continue;
          }

          // Slot popolato: muta proprietà
          slot.removeAttribute('hidden');
          slot.setAttribute('href', '/products/' + product.handle);
          slot.setAttribute('aria-label', 'Vai a ' + product.title);

          const img = slot.querySelector('img');
          if (img) {
            const newSrc = product.imageUrl || '';
            // Aggiorniamo src solo se cambia, per evitare reload
            // dell'immagine (flicker visivo dell'immagine)
            if (img.getAttribute('src') !== newSrc) {
              img.setAttribute('src', newSrc);
              img.setAttribute('alt', product.title || '');
            }
          }

          const titleSlot = slot.querySelector('.skon-fs-sg-title');
          if (titleSlot && titleSlot.textContent !== product.title) {
            titleSlot.textContent = product.title;
          }

          const priceSlot = slot.querySelector('.skon-fs-sg-price');
          const priceOriginalEl = priceSlot?.querySelector('.skon-fs-sg-price-original');
          const priceDiscountedEl = priceSlot?.querySelector('.skon-fs-sg-price-discounted');
          if (priceSlot && priceOriginalEl && priceDiscountedEl) {
            // SKÖN custom (maggio 2026) — Reset prezzo allo stato base.
            // Il calcolo coupon-aware (`_enrichWithDiscounts`) verrà
            // chiamato dopo il mount e potrà sovrascrivere questi valori
            // se rileva uno sconto effettivo del coupon attivo.
            //
            // Stato base per prodotto in saldo di listino (compareAtPrice
            // > price): mostra entrambi gli span con `--has-discount`.
            // Stato base per prodotto fuori saldo: solo prezzo originale.
            if (product.isOnSale && product.compareFmt) {
              if (priceOriginalEl.textContent !== product.compareFmt) {
                priceOriginalEl.textContent = product.compareFmt;
              }
              if (priceDiscountedEl.textContent !== product.priceFmt) {
                priceDiscountedEl.textContent = product.priceFmt;
              }
              priceDiscountedEl.removeAttribute('hidden');
              priceSlot.classList.add('skon-fs-sg-price--has-discount');
            } else {
              if (priceOriginalEl.textContent !== product.priceFmt) {
                priceOriginalEl.textContent = product.priceFmt;
              }
              priceDiscountedEl.textContent = '';
              priceDiscountedEl.setAttribute('hidden', '');
              priceSlot.classList.remove('skon-fs-sg-price--has-discount');
            }
            // Salviamo i centesimi base per riferimento
            // dell'`_enrichWithDiscounts` (che parte sempre dal listino).
            priceSlot.dataset.basePriceCents = String(product.priceCents || 0);
          }

          const btn = slot.querySelector('.skon-fs-sg-action--add');
          if (btn) {
            btn.setAttribute('data-skon-fs-add', String(product.variantId));
            btn.setAttribute('data-skon-fs-handle', product.handle || '');
            btn.setAttribute('data-skon-fs-price', String(product.priceCents || 0));
            // Reset eventuali stati transitori (loading/added) ereditati
            // da un add-to-cart precedente sullo stesso slot
            btn.classList.remove('skon-fs-sg-btn--loading', 'skon-fs-sg-btn--added');
          }
        }

        // Salva la lista variantId correnti per il diff successivo
        shell.dataset.lastVariants = products.map(function(p) { return p.variantId; }).join('|');
      },

      /**
       * SKÖN custom (maggio 2026) — Coupon-aware live pricing.
       *
       * Calcola il prezzo reale dei prodotti del widget tenendo conto
       * dei codici sconto applicabili attualmente nel carrello del
       * cliente, e aggiorna inline il prezzo barrato/scontato delle card
       * già montate (pattern shell+update, no DOM rebuild).
       *
       * STRATEGIA:
       *   1. Legge i discount_codes attivi dal carrello reale via
       *      /cart.js (tokenless, no impatto sul cart).
       *   2. Skip totale se non ci sono codici applicabili.
       *   3. Per ogni slot del widget, controlla la cache in-memory
       *      keyed su (variantId, codes_signature). Se hit usa il
       *      valore cached. Se miss, accumula il variantId.
       *   4. Per gli slot in MISS, fa UNA SOLA chiamata Storefront API
       *      tokenless `cartCreate` con tutte le merchandiseIds + codes.
       *      Costo query basso (~50 per 6 line items, limite 1000).
       *   5. Per ogni line nel cart virtuale di risposta:
       *      - line.cost.subtotalAmount = prezzo unitario PRE-sconto
       *      - line.cost.totalAmount    = prezzo unitario POST-sconto
       *      Se totalAmount < subtotalAmount → sconto rilevato → aggiorna
       *      la card con barrato (subtotal) + ridotto (total).
       *      Se totalAmount === subtotalAmount → sconto non rilevato per
       *      questo prodotto (es. shipping discount o esclusione coupon).
       *   6. Tutto in cache, nessuna chiamata duplicata per cart change
       *      successivi che mantengono lo stesso codes_signature.
       *
       * SHIPPING DISCOUNTS:
       * Sono filtrati implicitamente: producono `totalAmount === subtotalAmount`
       * sulle line del cart virtuale (toccano solo lo shipping, che il
       * cart virtuale non calcola). Quindi nessuna card viene aggiornata
       * per shipping-only discounts → nessun effetto visivo errato.
       *
       * GRACEFUL DEGRADATION:
       * - Se la chiamata Storefront API fallisce o timeout → la card resta
       *   con il prezzo originale (= comportamento pre-feature). Nessun
       *   errore visibile al cliente.
       * - Se il modulo è chiamato in concorrenza (cart change rapidi),
       *   le risposte stale vengono scartate via `_enrichRunId`.
       */
      _discountCache: Object.create(null),
      _enrichRunId: 0,

      _enrichWithDiscounts: async function() {
        const shell = document.querySelector('[data-skon-fs-suggestions] [data-skon-fs-shell="1"]');
        if (!shell) return;

        const slots = Array.from(shell.querySelectorAll('[data-skon-fs-slot]')).filter(function(s) {
          return !s.hasAttribute('hidden');
        });
        if (slots.length === 0) return;

        // 1. Leggi discount_codes attivi dal cart reale.
        //    Usa il cache server-side cart già fetched dal modulo cart;
        //    non serve un'altra fetch /cart.js.
        const cart = SkonFS.cart._lastCart;
        const activeCodes = ((cart && cart.discount_codes) || [])
          .filter(function(d) { return d.applicable; })
          .map(function(d) { return d.code; });

        // 2. Skip se nessun codice attivo.
        if (activeCodes.length === 0) return;

        const codesSig = activeCodes.slice().sort().join('|');
        const self = this;
        const runId = ++this._enrichRunId;

        // 3. Mappa slot → variantId, raccoglie hit/miss della cache.
        const slotByVariant = {};
        const cacheHits = [];
        const cacheMisses = [];

        slots.forEach(function(slot) {
          const btn = slot.querySelector('[data-skon-fs-add]');
          const vid = btn && btn.getAttribute('data-skon-fs-add');
          if (!vid) return;
          slotByVariant[vid] = slot;
          const cacheKey = vid + '|' + codesSig;
          if (cacheKey in self._discountCache) {
            cacheHits.push({ vid: vid, slot: slot, cached: self._discountCache[cacheKey] });
          } else {
            cacheMisses.push(vid);
          }
        });

        // Applica subito gli hit (sincrono, no flash).
        cacheHits.forEach(function(h) {
          self._applyDiscountToCard(h.slot, h.cached);
        });

        // Niente miss → done.
        if (cacheMisses.length === 0) return;

        // 4. UNA chiamata Storefront API per tutti i miss.
        const shop = (window.Shopify && window.Shopify.shop) || location.hostname;
        const lines = cacheMisses.map(function(vid) {
          return { merchandiseId: 'gid://shopify/ProductVariant/' + vid, quantity: 1 };
        });

        const mutation =
          'mutation cartCreate($input: CartInput!) {' +
            'cartCreate(input: $input) {' +
              'cart {' +
                'lines(first: 10) {' +
                  'edges { node {' +
                    'merchandise { ... on ProductVariant { id } }' +
                    'cost {' +
                      'subtotalAmount { amount }' +
                      'totalAmount { amount }' +
                    '}' +
                  '} }' +
                '}' +
              '}' +
              'userErrors { field message }' +
            '}' +
          '}';

        let response;
        try {
          // SKÖN custom (28 mag 2026 — v1.1.1): versione API esternalizzata
          // in skon-fs-config (CFG.shopify.storefrontApiVersion). Fallback
          // '2026-04' di safety se la chiave manca (es. config non ancora
          // aggiornata in deploy). Versionare Shopify API è una pratica
          // necessaria perché le versioni vengono deprecate ogni ~12 mesi.
          const apiVersion = (CFG.shopify && CFG.shopify.storefrontApiVersion) || '2026-04';
          const httpResp = await fetch('https://' + shop + '/api/' + apiVersion + '/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query: mutation,
              variables: { input: { lines: lines, discountCodes: activeCodes } }
            })
          });
          response = await httpResp.json();
        } catch (e) {
          // Network error o CORS → degrada silenziosamente.
          return;
        }

        // Se nel frattempo è partita un'altra run (cart cambiato di nuovo),
        // scartiamo questa risposta stale.
        if (runId !== this._enrichRunId) return;

        const cartData = response && response.data && response.data.cartCreate && response.data.cartCreate.cart;
        if (!cartData || !cartData.lines) return;

        // 5. Per ogni line nella risposta, salva in cache + aggiorna card.
        cartData.lines.edges.forEach(function(edge) {
          const node = edge.node;
          const vid = node.merchandise && node.merchandise.id && node.merchandise.id.split('/').pop();
          if (!vid) return;
          const subtotalCents = Math.round(parseFloat(node.cost.subtotalAmount.amount) * 100);
          const totalCents    = Math.round(parseFloat(node.cost.totalAmount.amount) * 100);
          // hasDiscount = il coupon ha avuto effetto VISIBILE sulla riga.
          const hasDiscount = totalCents < subtotalCents;
          const result = {
            hasDiscount:  hasDiscount,
            originalCents: subtotalCents,
            finalCents:    totalCents,
          };
          // Cache permanente per questa coppia (vid, codes).
          self._discountCache[vid + '|' + codesSig] = result;
          const slot = slotByVariant[vid];
          if (slot) self._applyDiscountToCard(slot, result);
        });
      },

      /**
       * SKÖN custom (maggio 2026) — Applica il risultato del calcolo
       * coupon-aware a una singola card. Mutazione minimale: textContent
       * dei due span prezzo + classi/hidden, no DOM rebuild.
       *
       * Se il coupon NON ha effetto su questo prodotto (hasDiscount=false),
       * lascia il prezzo allo stato base (settato da _updateSuggestionsShell):
       *   - mostra solo originalCents (= base price), oppure
       *   - se prodotto in saldo di listino, mostra il barrato di listino
       *     già impostato da _updateSuggestionsShell (non viene toccato).
       */
      _applyDiscountToCard: function(slot, result) {
        const priceSlot = slot.querySelector('.skon-fs-sg-price');
        const originalEl = priceSlot && priceSlot.querySelector('.skon-fs-sg-price-original');
        const discountedEl = priceSlot && priceSlot.querySelector('.skon-fs-sg-price-discounted');
        if (!priceSlot || !originalEl || !discountedEl) return;

        if (!result.hasDiscount) {
          // Coupon non efficace su questo prodotto: lascia stato base.
          // Niente da fare: _updateSuggestionsShell ha già settato
          // o il prezzo singolo o il barrato di listino.
          return;
        }

        // Coupon efficace: mostra originale (subtotal) barrato + ridotto (total).
        // _formatEUR è una utility privata del modulo.
        const originalFmt = _formatEUR(result.originalCents);
        const discountedFmt = _formatEUR(result.finalCents);

        if (originalEl.textContent !== originalFmt) {
          originalEl.textContent = originalFmt;
        }
        if (discountedEl.textContent !== discountedFmt) {
          discountedEl.textContent = discountedFmt;
        }
        discountedEl.removeAttribute('hidden');
        priceSlot.classList.add('skon-fs-sg-price--has-discount');
      },

      /**
       * Mount/aggiornamento del widget nel cart drawer.
       *
       * Forza refresh sincrono prima del rendering per evitare state
       * stale (es. dopo qty update). Per chiamate "interne" dove la
       * cache è già fresca, usa `mountWidgetFast`.
       */
      mountWidget: async function() {
        await SkonFS.cart.refresh();
        await this.mountWidgetFast();
      },

      /**
       * SKÖN custom (maggio 2026) — REFACTOR.
       * Strategia: ENSURE-AND-UPDATE.
       *   1) Se lo shell non esiste nel placeholder, lo costruiamo
       *      una volta sola (con N slot vuoti).
       *   2) Calcoliamo il mode dal SkonFS.cart._buildState (single
       *      source of truth, garantisce coerenza con la progress bar).
       *   3) Fetchiamo i prodotti per il mode scelto.
       *   4) Mutiamo i nodi dello shell tramite _updateSuggestionsShell.
       *
       * Niente innerHTML di alto livello, niente distruzione/ricostruzione
       * di nodi su update. Zero flicker.
       *
       * Importante per il bug "mode sbagliato sotto-50%":
       * il mode viene letto da SkonFS.cart._buildState() che usa SEMPRE
       * la cache aggiornata di _lastSubtotal. Se il refresh non è ancora
       * finito, mountWidgetFast non parte (early return).
       */
      mountWidgetFast: async function() {
        const placeholders = document.querySelectorAll('[data-skon-fs-suggestions]');
        if (!placeholders.length) return;

        // Aspettiamo che la cache sia popolata almeno una volta
        if (SkonFS.cart._lastSubtotal === null) {
          _log('widget mount skipped: subtotal still loading');
          return;
        }

        // Single source of truth dello stato (mode incluso)
        const state = (SkonFS.cart._buildState && SkonFS.cart._buildState()) || null;
        if (!state) {
          _log('widget mount skipped: state not available');
          return;
        }

        const subtotal  = state.subtotal_cents;
        const threshold = state.threshold_cents;
        const gap       = state.gap_cents;
        const achieved  = state.achieved;
        const mode      = state.widget_mode;

        // Step 1: ensure shell esiste in tutti i placeholder.
        // (Il for-of su NodeList è supportato; in caso di placeholder
        // multipli, ognuno ha il suo shell indipendente.)
        const self = this;
        const shells = [];
        placeholders.forEach(function(el) {
          let shell = el.querySelector('[data-skon-fs-shell="1"]');
          if (!shell) {
            shell = self._buildSuggestionsShell();
            el.appendChild(shell);
          }
          shells.push(shell);
        });

        // Step 2: fetch prodotti per il mode scelto
        let products;
        if (mode === 'bestseller') {
          products = await this._selectBestseller();
        } else if (mode === 'related') {
          products = await this._selectRelated();
        } else {
          products = await this.selectProducts(gap);
        }

        // Step 3: muta lo shell con i prodotti
        shells.forEach(function(shell) {
          self._updateSuggestionsShell(shell, products, mode, achieved, gap);
        });

        // SKÖN custom (maggio 2026) — Coupon-aware live pricing.
        // Lancia l'enrichment in background DOPO il mount delle card.
        // Le card sono già visibili con i prezzi originali; l'eventuale
        // sostituzione con prezzi scontati avviene asincrona (no flash,
        // no blocking). Vedi `_enrichWithDiscounts` per dettagli.
        // Usiamo `setTimeout(0)` per disaccoppiare completamente
        // dall'evento del bus che ha triggerato il mount.
        setTimeout(function() {
          self._enrichWithDiscounts().catch(function(e) {
            _log('_enrichWithDiscounts error:', e);
          });
        }, 0);

        _log('widget updated: mode', mode, 'subtotal', subtotal, 'gap', gap, 'achieved', achieved, 'products', products.length);
      },

      /**
       * Strategia BESTSELLER: cart vuoto. Carica la collection bestseller
       * configurata (CFG.widgetModes.bestseller), filtra come per la
       * modalità free-shipping (no out-of-stock, no multi-variant),
       * limita a maxProducts. Niente filtro "già in carrello" perché
       * il cart è vuoto per definizione di questa modalità.
       */
      _selectBestseller: async function() {
        const bs = CFG.widgetModes && CFG.widgetModes.bestseller;
        if (!bs || !bs.collectionHandle) {
          _log('_selectBestseller: no bestseller collection configured');
          return [];
        }
        let pool = await this._loadCollection(bs.collectionHandle);
        if (CFG.filler.excludeOutOfStock) {
          pool = pool.filter(function(p) { return p.available; });
        }
        if (CFG.filler.excludeMultiVariant) {
          pool = pool.filter(function(p) { return p.variantsCount <= 1; });
        }
        const max = (CFG.filler && CFG.filler.maxProducts) || 6;
        return pool.slice(0, max);
      },

      /**
       * Strategia RELATED: cart con prodotti, troppo lontano dalla soglia
       * free-shipping (oppure soglia raggiunta). Pesca correlati ai top N
       * item del cart per priceCents discendente, deduplica, applica filtri
       * standard, ordina per priceCents crescente (dal più economico al più
       * costoso, come da specifica).
       */
      _selectRelated: async function() {
        const cart = SkonFS.cart._lastCart;
        if (!cart || !cart.items || !cart.items.length) {
          _log('_selectRelated: empty cart, fallback to bestseller');
          return this._selectBestseller();
        }

        // Top N item del cart per prezzo unitario decrescente.
        // Usiamo final_price (post-sconto) come proxy del "valore percepito".
        const sortedItems = cart.items.slice().sort(function(a, b) {
          return (b.final_price || 0) - (a.final_price || 0);
        });
        const maxCalls = (CFG.widgetModes && CFG.widgetModes.relatedMaxApiCalls) || 2;
        const topProductIds = sortedItems.slice(0, maxCalls).map(function(i) { return i.product_id; });

        let pool = await this._loadRelated(topProductIds);

        // Esclusione prodotti già in carrello (sempre, per RELATED)
        const cartProductIds = cart.items.map(function(i) { return i.product_id; });
        pool = pool.filter(function(p) { return cartProductIds.indexOf(p.id) === -1; });

        // Filtri standard
        if (CFG.filler.excludeOutOfStock) {
          pool = pool.filter(function(p) { return p.available; });
        }
        if (CFG.filler.excludeMultiVariant) {
          pool = pool.filter(function(p) { return p.variantsCount <= 1; });
        }

        // Fallback: se nessun correlato, ripieghiamo su bestseller
        if (!pool.length) {
          _log('_selectRelated: no related products after filters, fallback to bestseller');
          return this._selectBestseller();
        }

        // Ordinamento dal più economico al più costoso (specifica esplicita)
        pool.sort(function(a, b) { return a.priceCents - b.priceCents; });

        const max = (CFG.filler && CFG.filler.maxProducts) || 6;
        return pool.slice(0, max);
      },

      /**
       * Renderizza skeleton placeholder (silhouette grigie con shimmer).
       * Usato durante il re-mount async per dare feedback visivo immediato
       * mentre selectProducts() carica i prodotti aggiornati.
       */
      renderSkeleton: function() {
        const cardCount = (CFG.filler && CFG.filler.maxProducts) || 6;
        let cards = '';
        for (let i = 0; i < cardCount; i++) {
          cards +=
            '<div class="skon-fs-sg-card skon-fs-sg-card--skeleton">' +
              '<div class="skon-fs-sg-img skon-fs-sg-skeleton-block"></div>' +
              '<div class="skon-fs-sg-body">' +
                '<div class="skon-fs-sg-skeleton-line skon-fs-sg-skeleton-line--title"></div>' +
                '<div class="skon-fs-sg-skeleton-line skon-fs-sg-skeleton-line--price"></div>' +
              '</div>' +
            '</div>';
        }
        return [
          '<div class="skon-fs-suggestions skon-fs-suggestions--loading">',
            '<div class="skon-fs-sg-header">',
              '<div class="skon-fs-sg-skeleton-line skon-fs-sg-skeleton-line--title-main"></div>',
              '<div class="skon-fs-sg-skeleton-line skon-fs-sg-skeleton-line--subtitle"></div>',
            '</div>',
            '<div class="skon-fs-sg-grid">' + cards + '</div>',
          '</div>'
        ].join('');
      },

      /**
       * SKÖN custom (maggio 2026) — Feedback aptico breve su Android.
       * Pattern uniforme a skon-quiz.js: chiamata diretta inline,
       * senza try/catch, navigator.vibrate è no-op su iOS/Safari/desktop.
       * 15ms = tap breve (pattern Material Design).
       */
      _hapticTap: function() {
        if (navigator.vibrate) navigator.vibrate(15);
      },

      /**
       * SKÖN custom (maggio 2026) — Highlight visivo temporaneo della
       * riga del prodotto appena aggiunto nel cart drawer.
       *
       * Strategia di identificazione della riga:
       * Le righe del cart non hanno data-variant-id, ma ognuna contiene
       * un link prodotto "/products/{handle}?variant={variantId}". Cerchiamo
       * la riga il cui link contiene il variantId del prodotto aggiunto.
       *
       * Tempistica: il caller fa morphing del cart-items-component PRIMA
       * di chiamare questa funzione, quindi la riga è già nel DOM. Se per
       * qualunque motivo non la troviamo (race), il pulse semplicemente
       * non si attiva — non rompiamo nulla.
       *
       * Implementazione: aggiungiamo classe .skon-fs-row-pulsing che
       * triggera animazione CSS keyframe (~1.5s, fade in/out gialino).
       * La classe si auto-rimuove a fine animazione via animationend.
       */
      _pulseAddedRow: function(variantId) {
        if (!variantId) return;
        // Aspettiamo un tick perché il morphing potrebbe essere appena finito
        setTimeout(function() {
          const variantStr = String(variantId);
          const rows = document.querySelectorAll('cart-drawer-component .cart-items__table-row');
          rows.forEach(function(row) {
            const link = row.querySelector('a[href*="variant=' + variantStr + '"]');
            if (link) {
              row.classList.add('skon-fs-row-pulsing');
              const cleanup = function() {
                row.classList.remove('skon-fs-row-pulsing');
                row.removeEventListener('animationend', cleanup);
              };
              row.addEventListener('animationend', cleanup);
              // Safety timeout in caso animationend non si scateni
              setTimeout(cleanup, 2000);
            }
          });
        }, 100);
      },

      /**
       * Handle click su pulsante "Aggiungi" (delegated).
       *
       * STRATEGIA: usiamo l'API Shopify Sections con il sectionId del
       * cart-items-component. Shopify ritornerà il nuovo HTML renderizzato
       * della section, che noi morfiamo nel DOM. Questo è lo stesso pattern
       * che usa `component-cart-items.js` del tema quando l'utente cambia
       * quantità: garantisce che il drawer si aggiorni visivamente in modo
       * coerente con quanto fa il tema nativamente.
       */
      handleAddClick: async function(button) {
        const variantId = button.getAttribute('data-skon-fs-add');
        const handle    = button.getAttribute('data-skon-fs-handle');
        const priceCents = parseInt(button.getAttribute('data-skon-fs-price'), 10) || 0;
        if (!variantId) return;

        // v1.1 fix — cattura subtotale e tipo visitatore PRIMA della fetch.
        // Serve per il payload tracking 'skon-fs:filler-added' (subtotal_before
        // deve essere il valore precedente all'aggiunta del prodotto).
        // Tolleriamo che SkonFS non sia disponibile (fallback a 0/'').
        const subtotalBefore = (window.SkonFS && window.SkonFS.cart && typeof window.SkonFS.cart._lastSubtotal === 'number')
          ? window.SkonFS.cart._lastSubtotal
          : 0;
        const visitorType = (window.SkonFS && window.SkonFS.id && typeof window.SkonFS.id.getVisitorType === 'function')
          ? window.SkonFS.id.getVisitorType()
          : '';

        // SKÖN custom (maggio 2026) — Feedback aptico immediato al tap.
        // Posizionato qui (prima di qualunque await) perché deve essere
        // sincrono con la pressione del bottone, non con la conferma di
        // avvenuta aggiunta. La conferma di "prodotto aggiunto" è invece
        // demandata al pulse visivo gialino (vedi _pulseAddedRow chiamato
        // dopo che /cart/add.js ha risposto e il morphing è completato).
        this._hapticTap();

        // Stato visivo: loading
        button.disabled = true;
        button.textContent = 'Aggiungo...';
        button.classList.add('skon-fs-sg-btn--loading');

        // Recupera il sectionId del cart-items-component dal DOM.
        // Se mancasse (es. drawer non ancora renderizzato), aggiungiamo
        // comunque al carrello ma il drawer non si aggiornerà — fallback OK.
        const cartItemsEl = document.querySelector('cart-items-component');
        const sectionId   = cartItemsEl ? cartItemsEl.dataset.sectionId : null;

        // Raccogli TUTTE le sectionId di tutti i cart-items-component
        // (potrebbero essere più di uno se c'è anche il cart in pagina)
        const allSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map(function(el) { return el.dataset.sectionId; })
          .filter(Boolean);

        try {
          const body = { id: variantId, quantity: 1 };
          if (allSectionIds.length) body.sections = allSectionIds.join(',');

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(body),
          });

          if (!res.ok) throw new Error('add failed');
          const responseData = await res.json();

          // Re-render delle section ricevute nel response.
          // Shopify ci ritorna `sections: { sectionId: htmlString }` quando
          // includiamo `sections` nel body.
          //
          // STRATEGIA: NON sostituiamo l'intero wrapper <section> perché
          // questo distrugge il <cart-drawer-component> (web component)
          // perdendo il suo stato "open". Invece, morfiamo solo i nodi
          // INTERNI mirati che cambiano: cart-items-component e
          // cart-drawer__summary. Questo è equivalente al morphSection
          // del tema (che è private, non esposto).
          if (responseData.sections) {
            for (const sId in responseData.sections) {
              const newHtml = responseData.sections[sId];
              if (!newHtml) continue;

              // Parse della nuova HTML in un DOM detached
              const tmp = document.createElement('div');
              tmp.innerHTML = newHtml;

              // (1) Aggiorna la cart-items-component (lista prodotti + summary interno)
              const newCartItems = tmp.querySelector('cart-items-component');
              const oldCartItems = document.querySelector(
                'cart-drawer-component cart-items-component[data-section-id="' + sId + '"]'
              ) || document.querySelector('cart-items-component[data-section-id="' + sId + '"]');

              if (newCartItems && oldCartItems) {
                // Copia attributi + innerHTML del nuovo nel vecchio.
                // Manteniamo l'ELEMENTO esistente (web component) ma rimpiazziamo
                // il contenuto: questo preserva eventuali listener globali e
                // soprattutto preserva il <cart-drawer-component> wrapper.
                oldCartItems.innerHTML = newCartItems.innerHTML;

                // Copia anche eventuali nuovi attributi (es. nuovi data-*)
                Array.from(newCartItems.attributes).forEach(function(attr) {
                  if (attr.name !== 'class' && oldCartItems.getAttribute(attr.name) !== attr.value) {
                    oldCartItems.setAttribute(attr.name, attr.value);
                  }
                });

                _log('cart-items morphed for section', sId);
              } else {
                _log('cart-items not found for morphing, section', sId);
              }
            }
          }

          // Tracking event 'skon-fs:filler-added'.
          // v1.1 fix — payload con chiavi snake_case (convenzione listener)
          // + 2 campi prima mancanti (subtotal_before_cents, visitor_type).
          // Mantenute anche le vecchie chiavi camelCase per backward-compat
          // verso eventuali ascoltatori esterni v1.0.
          const title = responseData.product_title || handle;
          document.dispatchEvent(new CustomEvent('skon-fs:filler-added', {
            detail: {
              // v1.1 — convenzione snake_case (preferred, letta da skon-fs-test.js)
              product_handle:        handle,
              variant_id:            variantId,
              price_cents:           priceCents,
              title:                 title,
              subtotal_before_cents: subtotalBefore,
              visitor_type:          visitorType,

              // v1.0 — convenzione camelCase (deprecata, mantenuta per backward-compat)
              productHandle:         handle,
              variantId:             variantId,
              priceCents:            priceCents,
            }
          }));

          // SKÖN custom (maggio 2026) — Feedback visivo di avvenuta
          // aggiunta: highlight gialino temporaneo della riga prodotto
          // nel cart. Il pulse viene chiamato DOPO il morphing del
          // cart-items-component (eseguito sopra), perché serve che la
          // nuova riga sia già nel DOM. Per le aggiunte di una variante
          // già in cart (qty++), il morphing aggiorna la quantità della
          // riga esistente: il pulse evidenzia comunque quella riga.
          //
          // Nota: l'haptic feedback NON viene chiamato qui. È stato
          // spostato all'inizio di handleAddClick così da scattare al
          // tap del bottone (sincrono) invece che alla conferma async
          // dell'add. La separazione è voluta: haptic = conferma di
          // pressione, pulse visivo = conferma di aggiunta avvenuta.
          this._pulseAddedRow(variantId);

          // Forza refresh del subtotal interno (aggiorna progress bar + widget)
          if (window.SkonFS && SkonFS.cart && typeof SkonFS.cart.refresh === 'function') {
            await SkonFS.cart.refresh();
          }

          // Re-mount widget dopo refresh (necessario perché replaceWith del
          // wrapper section ha rimpiazzato anche i nostri placeholder)
          setTimeout(function() {
            SkonFS.ui.mountProgressBar();
            SkonFS.suggestions.mountWidget();
          }, 50);

        } catch (e) {
          _log('add to cart failed', e);
          button.textContent = 'Errore — riprova';
          button.disabled = false;
          button.classList.remove('skon-fs-sg-btn--loading');
        }
      },
    };


    // ──────────────────────────────────────────────────────────────────
    // Hooks: re-mount widget tramite event bus
    // ──────────────────────────────────────────────────────────────────

    // SKÖN custom (maggio 2026) — REFACTOR ARCHITETTURALE.
    //
    // Il widget si aggancia all'evento sintetico 'skon-fs:state-changed'
    // emesso dal modulo SkonFS.cart ad ogni refresh (vedi
    // SkonFS.cart.refresh in skon-fs-test.js). Niente più listener
    // diretti su 'cart:update', 'cart:add', etc. da questo modulo:
    // single source of truth.
    //
    // Razionale: prima del refactor, sia skon-fs-test sia
    // skon-fs-suggestions ascoltavano i 4 cart events del tema in
    // parallelo, e per UN cart change si verificavano 2-4 chiamate
    // sovrapposte a mountWidget = flicker visibile delle card.
    // Ora c'è una sola fonte di trigger (SkonFS.bus) e niente flicker.
    //
    // L'evento 'skon-fs:state-changed' arriva con il payload completo
    // (subtotal, threshold, achieved, mode...), ma mountWidgetFast
    // legge comunque dallo state via SkonFS.cart._buildState() per
    // garantire coerenza (il payload e la cache sono sempre
    // sincronizzati al momento dell'emit).
    if (SkonFS.bus && typeof SkonFS.bus.on === 'function') {
      SkonFS.bus.on('skon-fs:state-changed', function() {
        SkonFS.suggestions.mountWidgetFast();
      });
    }

    // Mount iniziale: scatena il primo refresh (se non l'ha già fatto
    // il modulo skon-fs-test) e popola lo shell. Da qui in poi, ogni
    // cart change rifluisce via 'skon-fs:state-changed'.
    SkonFS.suggestions.mountWidget();

    // Click delegation per pulsanti "Aggiungi".
    // SKÖN custom (maggio 2026) — preventDefault + stopPropagation:
    // il bottone "+" è dentro un <a> link che porta alla PDP. Senza
    // questi due blocchi, click sul "+" attiverebbe ANCHE la navigazione
    // alla PDP (perdendo l'utente dal drawer e rendendo invisibile
    // l'add-to-cart).
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[data-skon-fs-add]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        SkonFS.suggestions.handleAddClick(btn);
      }
    });

    // SKÖN custom (maggio 2026) — Feedback aptico unificato per le
    // azioni di modifica cart eseguite dai controlli del tema:
    //   - .quantity-plus    → pulsante "+" del selettore quantità
    //   - .quantity-minus   → pulsante "-" del selettore quantità
    //   - .cart-items__remove → cestino di rimozione riga
    //
    // Coerente con skon-quiz.js (pattern inline navigator.vibrate(15)).
    // Listener globale, ma il selector limita lo scope al cart drawer
    // (closest('cart-drawer-component')) per non far vibrare azioni
    // simili su altre pagine non-cart.
    //
    // NON aggiunge pulse visivo gialino: quello è riservato alle
    // aggiunte dal widget cross-sell (segnale "nuovo prodotto"), non
    // alle modifiche di routine sulle righe esistenti.
    document.addEventListener('click', function(e) {
      const ctrl = e.target.closest('.quantity-plus, .quantity-minus, .cart-items__remove');
      if (!ctrl) return;
      if (!ctrl.closest('cart-drawer-component')) return;
      if (navigator.vibrate) navigator.vibrate(15);
    });

    _log('initialized');
  }


  // Bootstrap
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { _waitForSkonFS(_init); });
  } else {
    _waitForSkonFS(_init);
  }

})();